export default class ProductDetail {
    constructor() {
        this.leaderLines = [];
        this.anchors = [];
        this.zoomTargets = [];

        this.init();
    }

    init() {
        this.initBackButton();
        this.initZoomPointers();
        this.initAOS();
        this.initThumbnailSwiper();
    }

    initAOS() {
        const container = document.querySelector(".no-product-wrap");

        if (!container) {
            console.warn("[ProductDetail] Container not found");
            return;
        }

        container
            .querySelectorAll('[data-use-aos="true"]')
            .forEach((element) => {
                element.setAttribute("data-aos", "fade-up");
                element.setAttribute("data-aos-duration", "1000");
                element.setAttribute("data-aos-once", "true");
            });
    }

    /**
     * Initialize back button with preserved query parameters
     */
    initBackButton() {
        console.log("[ProductDetail] initBackButton()");
        const backButton = document.querySelector(".no-product-detail__back");

        if (!backButton) {
            console.warn("[ProductDetail] Back button not found");
            return;
        }

        // 기존 href에서 쿼리 부분 전부 제거하고 순수한 경로만 가져오기
        const originalHref = backButton.getAttribute("href") || "./index.php";
        const basePath = originalHref.split("?")[0]; // 쿼리 부분 전부 제거

        // 현재 페이지의 쿼리 파라미터에서 허용된 파라미터만 추출
        const currentParams = new URLSearchParams(window.location.search);
        const allowedParams = [
            "board_no",
            "search",
            "cursor",
            "loaded",
            "category",
            "scroll_to",
        ];

        // 새로운 쿼리 파라미터 생성 (기존 쿼리 전부 삭제 후 새로 추가)
        const newParams = new URLSearchParams();
        allowedParams.forEach((param) => {
            const value = currentParams.get(param);
            if (value) {
                newParams.append(param, value);
            }
        });

        // 쿼리 문자열 생성 및 href 설정
        const queryString = newParams.toString();
        backButton.href = queryString ? `${basePath}?${queryString}` : basePath;
    }

    /**
     * Initialize zoom pointers with leader lines
     * Dynamically finds anchors and their corresponding zoom targets
     */
    initZoomPointers() {
        console.log("[ProductDetail] initZoomPointers()");

        // Find all anchor elements
        const anchorElements = document.querySelectorAll(
            ".no-product-detail__leader-anchor"
        );

        if (anchorElements.length === 0) {
            console.log("[ProductDetail] No zoom pointers found");
            return;
        }

        // Check if LeaderLine is available
        if (typeof LeaderLine === "undefined") {
            console.error("[ProductDetail] LeaderLine library is not loaded");
            return;
        }

        // Find all zoom target elements
        const zoomElements = document.querySelectorAll(
            ".no-product-detail__section-feature-image"
        );

        // Match anchors with zoom targets
        anchorElements.forEach((anchor, index) => {
            const anchorId = anchor.id;
            let zoomTarget = null;

            // Method 1: Check for data-zoom-target attribute (most flexible)
            const zoomTargetId = anchor.getAttribute("data-zoom-target");
            if (zoomTargetId) {
                zoomTarget =
                    document.getElementById(zoomTargetId) ||
                    document.querySelector(zoomTargetId);
            }

            // Method 2: Extract feature name from anchor ID and find zoom by ID pattern
            if (!zoomTarget && anchorId) {
                const featureName = anchorId.replace("anchor-", "");
                zoomTarget = document.getElementById(`zoom-${featureName}`);
            }

            if (!zoomTarget) {
                console.warn(
                    `[ProductDetail] Zoom target not found for anchor: ${anchorId}`
                );
                return;
            }

            this.anchors.push(anchor);
            this.zoomTargets.push(zoomTarget);

            // Store original desktop positions from style attribute
            const style = anchor.getAttribute("style") || "";
            const topMatch = style.match(/top:\s*([^;]+)/);
            const leftMatch = style.match(/left:\s*([^;]+)/);

            if (topMatch)
                anchor.setAttribute("data-desktop-top", topMatch[1].trim());
            if (leftMatch)
                anchor.setAttribute("data-desktop-left", leftMatch[1].trim());
        });

        if (this.anchors.length === 0) {
            console.log("[ProductDetail] No valid anchor-zoom pairs found");
            return;
        }

        console.log(
            `[ProductDetail] Found ${this.anchors.length} zoom pointer(s)`
        );

        // Update anchor positions and wait for image load
        this.updateAnchorPositions();

        const razorImage = document.getElementById("razor-main-image");
        if (razorImage && !razorImage.complete) {
            razorImage.addEventListener(
                "load",
                () => {
                    setTimeout(() => this.createLeaderLines(), 100);
                },
                { once: true }
            );
        } else {
            setTimeout(() => this.createLeaderLines(), 100);
        }

        // Setup resize handler
        this.setupResizeHandler();

        // Cleanup on page unload
        window.addEventListener("beforeunload", () => {
            this.destroy();
        });
    }

    /**
     * Update anchor positions based on screen size (mobile/desktop)
     */
    updateAnchorPositions() {
        const isMobile = window.innerWidth <= 768;

        this.anchors.forEach((anchor) => {
            if (isMobile) {
                // Mobile: use data-mobile-* attributes
                const mobileTop = anchor.getAttribute("data-mobile-top");
                const mobileLeft = anchor.getAttribute("data-mobile-left");

                if (mobileTop) anchor.style.top = mobileTop;
                if (mobileLeft) anchor.style.left = mobileLeft;
            } else {
                // Desktop: use data-desktop-* attributes or inline style
                const desktopTop = anchor.getAttribute("data-desktop-top");
                const desktopLeft = anchor.getAttribute("data-desktop-left");

                if (desktopTop) anchor.style.top = desktopTop;
                if (desktopLeft) anchor.style.left = desktopLeft;
            }
        });
    }

    /**
     * Create leader lines between anchors and zoom targets
     */
    createLeaderLines() {
        const isMobile = window.innerWidth <= 768;

        if (isMobile) {
            console.log(
                "[ProductDetail] Mobile view detected, skipping leader lines"
            );
            return;
        }

        if (this.anchors.length !== this.zoomTargets.length) {
            console.error(
                "[ProductDetail] Anchor and zoom target count mismatch"
            );
            return;
        }

        // Remove existing lines
        this.removeLeaderLines();

        // Wait for layout to stabilize
        requestAnimationFrame(() => {
            this.anchors.forEach((anchor, index) => {
                const zoomTarget = this.zoomTargets[index];

                if (!anchor || !zoomTarget) return;

                // Get socket positions from data attributes or use defaults
                const startSocket =
                    anchor.getAttribute("data-line-start") ||
                    (index === 0 ? "right" : "bottom");
                const endSocket =
                    anchor.getAttribute("data-line-end") ||
                    (index === 0 ? "top" : "left");

                const line = new LeaderLine(anchor, zoomTarget, {
                    color: "#DC143C",
                    size: 2.5,
                    path: "grid",
                    startSocket: startSocket,
                    endSocket: endSocket,
                    startPlug: "behind",
                    endPlug: "behind",
                    dropShadow: false,
                    gradient: false,
                });

                this.leaderLines.push(line);
            });

            console.log(
                `[ProductDetail] Created ${this.leaderLines.length} leader line(s)`
            );
        });
    }

    /**
     * Remove all leader lines
     */
    removeLeaderLines() {
        this.leaderLines.forEach((line) => {
            if (line) line.remove();
        });
        this.leaderLines = [];
    }

    /**
     * Setup resize handler for responsive behavior
     */
    setupResizeHandler() {
        let resizeTimer;

        window.addEventListener("resize", () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                this.updateAnchorPositions();

                const isMobile = window.innerWidth <= 768;

                if (isMobile) {
                    this.removeLeaderLines();
                } else {
                    if (this.leaderLines.length === this.anchors.length) {
                        // Update existing lines
                        this.leaderLines.forEach((line) => {
                            if (line) line.position();
                        });
                    } else {
                        // Recreate lines
                        this.createLeaderLines();
                    }
                }
            }, 250);
        });
    }

    /**
     * Initialize thumbnail swiper with thumbs functionality
     */
    initThumbnailSwiper() {
        console.log("[ProductDetail] initThumbnailSwiper()");

        const thumbSwiperEl = document.getElementById(
            "product-thumbnails-swiper"
        );
        const mainSwiperEl = document.getElementById("product-main-swiper");

        if (!thumbSwiperEl || !mainSwiperEl) {
            console.warn(
                "[ProductDetail] Thumbnail swiper or main swiper not found"
            );
            return;
        }

        // 썸네일 스와이퍼 초기화
        const prodThumbSwiper = new Swiper(thumbSwiperEl, {
            spaceBetween: 10,
            slidesPerView: "auto",
            freeMode: true,
            watchSlidesProgress: true,
            breakpoints: {
                320: {
                    spaceBetween: 8,
                },
                768: {
                    spaceBetween: 10,
                },
                1024: {
                    spaceBetween: 12,
                },
            },
        });

        // 메인 이미지 스와이퍼 초기화 (thumbs 연결)
        const prodSwiper = new Swiper(mainSwiperEl, {
            speed: 600,
            effect: "fade",
            fadeEffect: {
                crossFade: true,
            },
            setWrapperSize: true,
            spaceBetween: 10,
            navigation: {
                nextEl: ".no-product-detail__thumb-btn--next",
                prevEl: ".no-product-detail__thumb-btn--prev",
            },
            thumbs: {
                swiper: prodThumbSwiper,
            },
        });
    }

    /**
     * Cleanup on page unload
     */
    destroy() {
        this.removeLeaderLines();
        this.anchors = [];
        this.zoomTargets = [];
    }
}
